import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Badge,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  message,
  Drawer,
  Descriptions,
  List,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Tooltip,
  Dropdown,
  Menu,
  Image
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  ReloadOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api, { tasksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const TaskOverview = () => {
  const { user, token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [clients, setClients] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isTaskDrawerVisible, setIsTaskDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  
  // Multiple selection and bulk operations
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  
  // Add state for bulk delete confirmation modal
  const [isBulkDeleteModalVisible, setIsBulkDeleteModalVisible] = useState(false);
  
  // Check if user is superadmin
  const isSuperAdmin = user && user.role === 'super_admin';
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status_id: null,
    priority: null,
    assigned_to: null,
    client_id: null,
    file_status: null,
    archived: false,
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // Load initial data
  useEffect(() => {
    loadTaskStatuses();
    loadClients();
    loadStatistics();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      // Using services API with manual parameter handling
      const queryParams = new URLSearchParams(filters);
      const response = await api.get(`/api/tasks?${queryParams.toString()}`);
      setTasks(response.data.tasks);
      setPagination({
        current: response.data.pagination.page,
        pageSize: response.data.pagination.per_page,
        total: response.data.pagination.total
      });
    } catch (error) {
      message.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadTaskStatuses = async () => {
    try {
      const response = await api.get('/api/tasks/statuses');
      setTaskStatuses(response.data.statuses);
    } catch (error) {
      message.error('Failed to load task statuses');
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/api/tasks/clients');
      setClients(response.data.clients);
    } catch (error) {
      message.error('Failed to load clients');
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/api/tasks/statistics');
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleCreateTask = async (values) => {
    try {
      // Parse dates
      if (values.due_date) {
        values.due_date = values.due_date.toISOString();
      }

      await tasksAPI.createTask(values);
      message.success('Task created successfully');
      setIsTaskModalVisible(false);
      form.resetFields();
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, values) => {
    try {
      // Parse dates
      if (values.due_date) {
        values.due_date = values.due_date.toISOString();
      }
      if (values.completed_at && values.status_id === getCompletedStatusId()) {
        values.completed_at = moment().toISOString();
      }

      await tasksAPI.updateTask(taskId, values);
      message.success('Task updated successfully');
      setIsTaskModalVisible(false);
      form.resetFields();
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    Modal.confirm({
      title: 'Delete Task',
      content: 'Are you sure you want to delete this task? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await tasksAPI.deleteTask(taskId);
          message.success('Task deleted successfully');
          loadTasks();
          loadStatistics();
        } catch (error) {
          console.error('Delete error:', error);
          message.error('Failed to delete task: ' + (error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleViewTask = async (taskId) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}`);
      setSelectedTask(response.data.task);
      setIsTaskDrawerVisible(true);
    } catch (error) {
      message.error('Failed to load task details');
    }
  };

  const handleDownloadFile = async (taskId, fileId, filename) => {
    try {
      const response = await api.get(`/api/tasks/${taskId}/files/${fileId}/download`);
      
      if (response.data.download_url) {
        // For cloud storage, open URL in new tab
        window.open(response.data.download_url, '_blank');
      } else {
        // For local files, trigger download
        const link = document.createElement('a');
        link.href = `/api/tasks/${taskId}/files/${fileId}/download`;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      message.error('Failed to download file');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilters(prev => ({
      ...prev,
      page: pagination.current,
      per_page: pagination.pageSize,
      sort_by: sorter.field || 'created_at',
      sort_order: sorter.order === 'ascend' ? 'asc' : 'desc'
    }));
  };

  // Bulk operation handlers
  const handleBulkArchive = async () => {
    if (selectedTaskIds.length === 0) {
      message.warning('Please select tasks to archive');
      return;
    }

    setBulkOperationLoading(true);
    try {
      const response = await api.post('/api/tasks/bulk/archive', { task_ids: selectedTaskIds });
      message.success(response.data.message);
      setSelectedTaskIds([]);
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to archive tasks');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkUnarchive = async () => {
    if (selectedTaskIds.length === 0) {
      message.warning('Please select tasks to unarchive');
      return;
    }

    setBulkOperationLoading(true);
    try {
      const response = await api.post('/api/tasks/bulk/unarchive', { task_ids: selectedTaskIds });
      message.success(response.data.message);
      setSelectedTaskIds([]);
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to unarchive tasks');
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) {
      message.warning('Please select tasks to delete');
      return;
    }

    // Check if user is superadmin
    if (!isSuperAdmin) {
      message.error('Only superadmin can delete tasks');
      return;
    }

    setIsBulkDeleteModalVisible(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleteModalVisible(false);
    setBulkOperationLoading(true);
    try {
      const response = await api.post('/api/tasks/bulk/delete', { task_ids: selectedTaskIds });
      message.success(response.data.message);
      setSelectedTaskIds([]);
      loadTasks();
      loadStatistics();
    } catch (error) {
      console.error('Bulk delete error:', error);
      message.error('Failed to delete tasks: ' + error.message);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  const handleSelectChange = (selectedRowKeys) => {
    setSelectedTaskIds(selectedRowKeys);
  };

  const handleSelectAll = (selected, selectedRows, changeRows) => {
    if (selected) {
      setSelectedTaskIds(tasks.map(task => task.id));
    } else {
      setSelectedTaskIds([]);
    }
  };

  // Individual task archive/unarchive handlers
  const handleArchiveTask = async (taskId) => {
    try {
      await api.post('/api/tasks/bulk/archive', { task_ids: [taskId] });
      message.success('Task archived successfully');
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to archive task');
    }
  };

  const handleUnarchiveTask = async (taskId) => {
    try {
      await api.post('/api/tasks/bulk/unarchive', { task_ids: [taskId] });
      message.success('Task unarchived successfully');
      loadTasks();
      loadStatistics();
    } catch (error) {
      message.error('Failed to unarchive task');
    }
  };

  const getCompletedStatusId = () => {
    const completedStatus = taskStatuses.find(s => s.name.toLowerCase().includes('completed'));
    return completedStatus ? completedStatus.id : null;
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    
    let icon = <ClockCircleOutlined />;
    if (status.name.toLowerCase().includes('completed')) {
      icon = <CheckCircleOutlined />;
    } else if (status.name.toLowerCase().includes('progress')) {
      icon = <PlayCircleOutlined />;
    } else if (status.name.toLowerCase().includes('review')) {
      icon = <ExclamationCircleOutlined />;
    }

    return (
      <Badge 
        color={status.color || '#6B7280'} 
        text={
          <span style={{ color: status.color || '#6B7280' }}>
            {icon} {status.name}
          </span>
        }
      />
    );
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#ff4d4f',
      high: '#fa8c16',
      medium: '#1890ff',
      low: '#52c41a'
    };
    return colors[priority] || '#1890ff';
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      render: (text, record) => (
        <div>
          <Button type="link" onClick={() => handleViewTask(record.id)} style={{ padding: 0 }}>
            {text}
            {record.archived && (
              <Tag color="orange" size="small" style={{ marginLeft: '8px' }}>
                ARCHIVED
              </Tag>
            )}
          </Button>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
              {record.description.substring(0, 100)}{record.description.length > 100 ? '...' : ''}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusBadge(status)
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      sorter: true,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      width: 120,
      render: (client) => client ? client.name : <Text type="secondary">No client</Text>
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to_username',
      key: 'assigned_to',
      width: 120,
      render: (username) => username || <Text type="secondary">Unassigned</Text>
    },
    {
      title: 'Files',
      dataIndex: 'files',
      key: 'files',
      width: 150,
      render: (files, record) => {
        const fileCount = record.file_count || 0;
        
        if (!files || files.length === 0) {
          return (
            <span style={{ color: '#999' }}>
              <FileOutlined /> 0
            </span>
          );
        }
        
        // Count files by status
        const statusCounts = {};
        files.forEach(file => {
          let status = 'pending';
          if (file.upload_metadata) {
            try {
              const metadata = typeof file.upload_metadata === 'string' 
                ? JSON.parse(file.upload_metadata) 
                : file.upload_metadata;
              status = metadata.file_status || 'pending';
            } catch (e) {
              // ignore parsing errors
            }
          }
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        });
        
        const statusColors = {
          'pending': '#d9d9d9',
          'processing': '#1890ff',
          'completed': '#52c41a',
          'failed': '#ff4d4f',
          'archived': '#fa8c16'
        };
        
        return (
          <div>
            <div style={{ marginBottom: '2px' }}>
              <FileOutlined /> {fileCount}
            </div>
            <div style={{ fontSize: '11px' }}>
              {Object.entries(statusCounts).map(([status, count]) => (
                <Tag 
                  key={status} 
                  color={statusColors[status]} 
                  size="small" 
                  style={{ margin: '1px', fontSize: '10px' }}
                >
                  {status}: {count}
                </Tag>
              ))}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 120,
      sorter: true,
      render: (date) => {
        if (!date) return <Text type="secondary">No due date</Text>;
        
        const dueDate = moment(date);
        const isOverdue = dueDate.isBefore(moment()) && !this?.completed_at;
        
        return (
          <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
            {dueDate.format('MMM DD, YYYY')}
          </span>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      sorter: true,
      render: (date) => moment(date).format('MMM DD, YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewTask(record.id)}
            />
          </Tooltip>
          <Tooltip title="Edit Task">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => {
                setSelectedTask(record);
                form.setFieldsValue({
                  ...record,
                  due_date: record.due_date ? moment(record.due_date) : null,
                  status_id: record.status?.id,
                  client_id: record.client?.id
                });
                setIsTaskModalVisible(true);
              }}
            />
          </Tooltip>
          <Dropdown
            overlay={
              <Menu>
                {!record.archived ? (
                  <Menu.Item 
                    key="archive" 
                    onClick={() => handleArchiveTask(record.id)}
                  >
                    <DeleteOutlined /> Archive
                  </Menu.Item>
                ) : (
                  <Menu.Item 
                    key="unarchive" 
                    onClick={() => handleUnarchiveTask(record.id)}
                  >
                    <ReloadOutlined /> Unarchive
                  </Menu.Item>
                )}
                {/* Temporarily show delete for all users for testing */}
                <Menu.Item 
                  key="delete" 
                  danger
                  onClick={() => handleDeleteTask(record.id)}
                >
                  <DeleteOutlined /> Delete Permanently
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', height: '100vh', overflow: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Task Management</Title>
        
        {/* Statistics Cards */}
        {statistics && (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Tasks"
                  value={statistics.totals.total_tasks}
                  prefix={<FileOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Completed"
                  value={statistics.totals.completed_tasks}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Overdue"
                  value={statistics.totals.overdue_tasks}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="In Progress"
                  value={statistics.totals.total_tasks - statistics.totals.completed_tasks}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card style={{ marginBottom: '16px' }}>
          <Row gutter={16} align="middle">
            <Col span={5}>
              <Search
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onSearch={() => loadTasks()}
                enterButton={<SearchOutlined />}
              />
            </Col>
            <Col span={3}>
              <Select
                placeholder="Status"
                value={filters.status_id}
                onChange={(value) => handleFilterChange('status_id', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {taskStatuses.map(status => (
                  <Option key={status.id} value={status.id}>
                    {status.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Priority"
                value={filters.priority}
                onChange={(value) => handleFilterChange('priority', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="critical">Critical</Option>
                <Option value="high">High</Option>
                <Option value="medium">Medium</Option>
                <Option value="low">Low</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="Client"
                value={filters.client_id}
                onChange={(value) => handleFilterChange('client_id', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {clients.map(client => (
                  <Option key={client.id} value={client.id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="File Status"
                value={filters.file_status}
                onChange={(value) => handleFilterChange('file_status', value)}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="pending">Pending</Option>
                <Option value="processing">Processing</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="archived">Archived</Option>
              </Select>
            </Col>
            <Col span={3}>
              <Select
                placeholder="View"
                value={filters.archived ? 'archived' : 'active'}
                onChange={(value) => handleFilterChange('archived', value === 'archived')}
                style={{ width: '100%' }}
              >
                <Option value="active">Active Tasks</Option>
                <Option value="archived">Archived Tasks</Option>
              </Select>
            </Col>
            <Col span={5}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadTasks}>
                  Refresh
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedTask(null);
                    form.resetFields();
                    setIsTaskModalVisible(true);
                  }}
                >
                  New Task
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Tasks Table */}
      <Card style={{ marginBottom: '24px' }}>
        {/* Bulk Actions */}
        {selectedTaskIds.length > 0 && (
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f2f5', borderRadius: '6px' }}>
            <Space>
              <Text strong>{selectedTaskIds.length} task(s) selected</Text>
              {!filters.archived && (
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={handleBulkArchive}
                  loading={bulkOperationLoading}
                >
                  Archive Selected
                </Button>
              )}
              {filters.archived && (
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={handleBulkUnarchive}
                  loading={bulkOperationLoading}
                >
                  Unarchive Selected
                </Button>
              )}
              {/* Temporarily show delete for all users for testing */}
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                loading={bulkOperationLoading}
              >
                Delete Selected
              </Button>
              <Button
                type="default"
                onClick={() => setSelectedTaskIds([])}
              >
                Clear Selection
              </Button>
            </Space>
          </div>
        )}
        
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedTaskIds,
            onChange: handleSelectChange,
            onSelectAll: handleSelectAll,
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
            ],
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200, y: 'calc(100vh - 400px)' }}
          size="middle"
        />
      </Card>

      {/* Task Modal */}
      <Modal
        title={selectedTask ? 'Edit Task' : 'Create New Task'}
        open={isTaskModalVisible}
        onCancel={() => {
          setIsTaskModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            if (selectedTask) {
              handleUpdateTask(selectedTask.id, values);
            } else {
              handleCreateTask(values);
            }
          }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter task title' }]}
          >
            <Input placeholder="Enter task title" />
          </Form.Item>
          
          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={4} placeholder="Enter task description" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Status"
                name="status_id"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  {taskStatuses.map(status => (
                    <Option key={status.id} value={status.id}>
                      {status.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Priority"
                name="priority"
              >
                <Select placeholder="Select priority">
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="critical">Critical</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Client"
                name="client_id"
              >
                <Select placeholder="Select client" allowClear>
                  {clients.map(client => (
                    <Option key={client.id} value={client.id}>
                      {client.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="Due Date"
            name="due_date"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsTaskModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {selectedTask ? 'Update' : 'Create'} Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task Details Drawer */}
      <Drawer
        title="Task Details"
        placement="right"
        size="large"
        open={isTaskDrawerVisible}
        onClose={() => setIsTaskDrawerVisible(false)}
      >
        {selectedTask && (
          <div>
            <Descriptions title={selectedTask.title} bordered column={1}>
              <Descriptions.Item label="Status">
                {getStatusBadge(selectedTask.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(selectedTask.priority)}>
                  {selectedTask.priority?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {selectedTask.client ? selectedTask.client.name : <Text type="secondary">No client assigned</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {selectedTask.assigned_to_username || <Text type="secondary">Unassigned</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {selectedTask.created_by_username}
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {selectedTask.due_date ? moment(selectedTask.due_date).format('YYYY-MM-DD HH:mm') : 'No due date'}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {moment(selectedTask.created_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              {selectedTask.completed_at && (
                <Descriptions.Item label="Completed At">
                  {moment(selectedTask.completed_at).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedTask.description && (
              <div style={{ marginTop: '16px' }}>
                <Title level={4}>Description</Title>
                <p>{selectedTask.description}</p>
              </div>
            )}

            {selectedTask.files && selectedTask.files.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <Title level={4}>Attached Files ({selectedTask.files.length})</Title>
                <List
                  itemLayout="horizontal"
                  dataSource={selectedTask.files}
                  renderItem={(file) => {
                        // Extract file status and additional metadata from upload_metadata
                        let fileStatus = 'pending';
                        let fileVersion = file.version || 1;
                        let fileMd5Hash = null;
                        let statusUpdatedAt = null;
                        let versionComment = null;
                        let isLatestVersion = true;
                        
                        if (file.upload_metadata) {
                          try {
                            const metadata = typeof file.upload_metadata === 'string' 
                              ? JSON.parse(file.upload_metadata) 
                              : file.upload_metadata;
                            
                            fileStatus = metadata.file_status || 'pending';
                            fileMd5Hash = metadata.file_md5_hash || metadata.md5_hash;
                            statusUpdatedAt = metadata.status_updated_at;
                            versionComment = metadata.version_comment;
                            isLatestVersion = metadata.is_latest_version !== false;
                          } catch (e) {
                            console.warn('Error parsing upload_metadata:', e);
                          }
                        }
                        
                        // Check if file has preview metadata from database
                        let hasPreview = false;
                        if (file.preview_metadata) {
                          try {
                            const previewMeta = typeof file.preview_metadata === 'string'
                              ? JSON.parse(file.preview_metadata)
                              : file.preview_metadata;
                            // Check if preview data exists (either local or storage previews)
                            hasPreview = previewMeta && (
                              (previewMeta.local_previews && Object.keys(previewMeta.local_previews).length > 0) ||
                              (previewMeta.storage_previews && Object.keys(previewMeta.storage_previews).length > 0)
                            );
                          } catch (e) {
                            console.warn('Error parsing preview_metadata:', e);
                          }
                        }
                        
                        // Get status badge color and text
                        const getStatusBadge = (status) => {
                          const statusConfig = {
                            'pending': { color: 'default', text: 'Pending' },
                            'processing': { color: 'processing', text: 'Processing' },
                            'completed': { color: 'success', text: 'Completed' },
                            'failed': { color: 'error', text: 'Failed' },
                            'archived': { color: 'warning', text: 'Archived' }
                          };
                          const config = statusConfig[status] || { color: 'default', text: status };
                          return <Badge status={config.color} text={config.text} />;
                        };
                        
                        const getPreviewUrl = (fileId, size = 'thumbnail') => {
                          const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
                          return `${API_BASE_URL}/api/files/${fileId}/preview/${size}?token=${token}`;
                        };
                        
                        return (
                          <List.Item
                            actions={[
                              <Button
                                type="link"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadFile(selectedTask.id, file.id, file.original_filename)}
                              >
                                Download
                              </Button>
                            ]}
                          >
                            <List.Item.Meta
                              avatar={
                                hasPreview ? (
                                  <Image
                                    src={getPreviewUrl(file.id, 'thumbnail')}
                                    alt={file.original_filename}
                                    width={60}
                                    height={60}
                                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                                    preview={{
                                      src: getPreviewUrl(file.id, 'large')
                                    }}
                                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                                  />
                                ) : (
                                  <FileOutlined style={{ fontSize: '24px' }} />
                                )
                              }
                              title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span>{file.original_filename}</span>
                                  {hasPreview && <FileImageOutlined style={{ color: '#1890ff' }} />}
                                  {!isLatestVersion && (
                                    <Tag color="orange" size="small">v{fileVersion}</Tag>
                                  )}
                                  {isLatestVersion && fileVersion > 1 && (
                                    <Tag color="blue" size="small">v{fileVersion} (Latest)</Tag>
                                  )}
                                </div>
                              }
                              description={
                                <div>
                                  <div style={{ marginBottom: '4px' }}>
                                    <strong>Status: </strong>
                                    {getStatusBadge(fileStatus)}
                                  </div>
                                  <div>Size: {(file.file_size / 1024).toFixed(1)} KB</div>
                                  <div>Type: {file.mime_type}</div>
                                  <div>Storage: {file.storage_type}</div>
                                  <div>Uploaded: {moment(file.created_at).format('YYYY-MM-DD HH:mm')}</div>
                                  {statusUpdatedAt && (
                                    <div>Status Updated: {moment(statusUpdatedAt).format('YYYY-MM-DD HH:mm')}</div>
                                  )}
                                  {versionComment && (
                                    <div>Comment: {versionComment}</div>
                                  )}
                                  {fileMd5Hash && (
                                    <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#666' }}>
                                      MD5: {fileMd5Hash}
                                    </div>
                                  )}
                                </div>
                              }
                            />
                          </List.Item>
                        );
                      }}
                    />
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        title="Delete Tasks"
        open={isBulkDeleteModalVisible}
        onOk={confirmBulkDelete}
        onCancel={() => setIsBulkDeleteModalVisible(false)}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={bulkOperationLoading}
      >
        <p>Are you sure you want to permanently delete {selectedTaskIds.length} task(s)? This will also remove all associated files and cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default TaskOverview;
